<template>
  <div
    ref="convstooltip"
    class="con-vs-tooltip"
    @mouseenter="mouseenterx"
    @mouseleave="mouseleavex"
    @mouseup="destroy"
  >
    <transition name="tooltip-fade">
      <div
        v-show="tooltipActive && active"
        ref="vstooltip"
        :class="[
                    `vs-tooltip-${positionx || position}`,
                    `vs-tooltip-${color}`,
                    { 'after-none': noneAfter },
                    { 'vs-tooltip--breakable': textBreakable },
                ]"
        :style="style"
        class="vs-tooltip"
      >
        <h4 v-if="title">{{ title }}</h4>
        <div :class="{
                  'vs-tooltip--text--overflowstyle': !textBreakable,
                  'vs-tooltip--text--breakablestyle': textBreakable,
                }"
        >{{ text }}
        </div>
      </div>
    </transition>
    <slot></slot>
  </div>
</template>
<script>
import utils from '@assets/utils';
import _color from '@assets/utils/color.js';
import { clampHorizontal } from '@/procedures/tooltip-position';

export default {
  name: 'vx-tooltip',
  props: {
    title: {
      default: null,
      type: [String, Number],
    },
    text: {
      default: null,
      type: [String, Number],
    },
    color: {
      default: null,
      type: String,
    },
    position: {
      default: 'top',
      type: String,
    },
    delay: {
      default: '0s',
      type: [Number, String],
    },
    tooltipActive: {
      default: true,
    },
    textBreakable: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    cords: {},
    active: false,
    widthx: 'auto',
    positionx: null,
    noneAfter: false,
  }),
  computed: {
    style() {
      return {
        left: this.cords.left,
        top: this.cords.top,
        transitionDelay: this.active ? this.delay : '0s',
        background: _color.getColor(this.color, 1),
        width: this.widthx,
      };
    },
  },
  methods: {
    mouseenterx() {
      this.active = true;
      this.$nextTick(() => {
        if (!this.$refs.vstooltip) {
          return;
        }
        utils.insertBody(this.$refs.vstooltip);
        this.changePosition(
          this.$refs.convstooltip,
          this.$refs.vstooltip,
        );
      });
    },
    mouseleavex() {
      this.active = false;
    },
    changePosition(elxEvent, tooltip) {
      this.noneAfter = false;
      this.positionx = null;
      let elx = elxEvent.closest('.con-vs-tooltip');
      let scrollTopx =
        window.pageYOffset || document.documentElement.scrollTop;
      let topx =
        elx.getBoundingClientRect().top +
        scrollTopx -
        tooltip.clientHeight -
        4;
      let leftx =
        elx.getBoundingClientRect().left -
        tooltip.clientWidth / 2 +
        elx.clientWidth / 2;
      let widthx = elx.clientWidth;

      if (this.position == 'bottom') {
        topx =
          elx.getBoundingClientRect().top +
          scrollTopx +
          elx.clientHeight +
          4;
      } else if (this.position == 'left') {
        leftx =
          elx.getBoundingClientRect().left - tooltip.clientWidth - 4;
        topx =
          elx.getBoundingClientRect().top +
          scrollTopx +
          elx.clientHeight / 2 -
          tooltip.clientHeight / 2;
        if (Math.sign(leftx) == -1) {
          leftx = elx.getBoundingClientRect().left;
          topx =
            elx.getBoundingClientRect().top +
            scrollTopx +
            elx.clientHeight +
            4;
          this.positionx = 'bottom';
          this.noneAfter = true;
        }
      } else if (this.position == 'right') {
        leftx = elx.getBoundingClientRect().left + elx.clientWidth + 4;
        topx =
          elx.getBoundingClientRect().top +
          scrollTopx +
          elx.clientHeight / 2 -
          tooltip.clientHeight / 2;
        if (window.innerWidth - (leftx + tooltip.clientWidth) <= 20) {
          leftx =
            elx.getBoundingClientRect().left -
            tooltip.clientWidth / 2 -
            10;
          topx =
            elx.getBoundingClientRect().top +
            scrollTopx +
            elx.clientHeight +
            4;
          this.positionx = 'bottom';
          this.noneAfter = true;
        }
      }
      /*
       * PUB-1649 — dymek trafia do `body`, więc nie tnie go żaden `overflow`, ale bez tego
       * zacisku potrafi wyjechać poza ekran: `leftx` centruje go na elemencie wyzwalającym,
       * a przy szerokiej pomocy i kontrolce blisko krawędzi wychodzi wartość ujemna albo
       * większa niż okno. Zacisk dotyczy wyłącznie POZIOMU i nie zmienia wybranej strony.
       *
       * Code review: zacisk obowiązuje WYŁĄCZNIE wariant `text-breakable` — tylko on dostał
       * podniesione `max-width` i tylko on potrafi wyjechać poza ekran. Zwykłe dymki zachowują
       * dotychczasowe pozycjonowanie co do piksela, żeby poprawka szerokiej pomocy nie przesunęła
       * setek istniejących tooltipów (m.in. AttendantCounterChip).
       */
      if (this.textBreakable) {
        leftx = clampHorizontal(leftx, tooltip.clientWidth, window.innerWidth);
      }

      this.cords = {
        left: `${ leftx }px`,
        top: `${ topx }px`,
        width: `${ widthx }px`,
      };
    },
    removeTooltip() {
      this.active = false;
      this.$nextTick(() => {
        if (!this.$refs.vstooltip) {
          return;
        }
        if (this.active) {
          utils.removeBody(this.$refs.vstooltip);
        }
      });
    },
    destroy() {
      this.removeTooltip();
    },
  },
};
</script>

<style lang="scss" scoped>
.vs-tooltip {
  .vs-tooltip--text--overflowstyle {
    overflow-wrap: anywhere;
  }

  /*
   * PUB-1649 — `pre` zachowywało podział na zdania, ale ZABRANIAŁO zawijania, więc zdanie
   * dłuższe niż `max-width` dymka (220 px z vuesax) wychodziło poza ciemne tło i stawało się
   * nieczytelne. `pre-line` zachowuje autorskie `\n` i dopuszcza zawinięcie, gdy linia
   * nie mieści się w dymku — treść pomocy pozostaje ta sama, zmienia się wyłącznie łamanie.
   */
  .vs-tooltip--text--breakablestyle {
    white-space: pre-line;
  }

  /*
   * Wielozdaniowa pomoc potrzebuje szerszego dymka niż 220 px, inaczej każde zdanie zawija się
   * po dwa–trzy słowa. Podnosimy `max-width` WYŁĄCZNIE dla wariantu `text-breakable`, więc
   * zwykłe dymki (etykiety przycisków, statusy) zostają bez zmian.
   */
  &.vs-tooltip--breakable {
    max-width: 30rem;
  }
}

/* Na wąskim ekranie szerokość dymka ogranicza samo okno, nie stała. */
@media (max-width: 34rem) {
  .vs-tooltip.vs-tooltip--breakable {
    max-width: calc(100vw - 1rem);
  }
}
</style>
